import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in';
  delay?: number;
  threshold?: number;
}

const AnimatedSection = ({
  children,
  className,
  animation = 'fade-in',
  delay = 0,
  threshold = 0.1,
}: AnimatedSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay, threshold]);

  const animationClasses = {
    'fade-in': isVisible ? 'animate-fade-in' : 'opacity-0 translate-y-8',
    'slide-up': isVisible ? 'animate-slide-in-up' : 'opacity-0 translate-y-12',
    'slide-left': isVisible ? 'animate-slide-in-left' : 'opacity-0 -translate-x-12',
    'slide-right': isVisible ? 'animate-slide-in-right' : 'opacity-0 translate-x-12',
    'scale-in': isVisible ? 'animate-scale-in' : 'opacity-0 scale-90',
  };

  return (
    <div
      ref={ref}
      className={cn(
        'transition-all duration-700 ease-out',
        animationClasses[animation],
        className
      )}
    >
      {children}
    </div>
  );
};

export default AnimatedSection;
